import { DatePicker } from '@mui/x-date-pickers';
import { useState, useMemo, useEffect } from 'react';
import dayjs, { Dayjs } from 'dayjs';

import { TextInput } from '@components/Inputs';
import { Select } from '@components/Inputs/Select';
import { useFilterSurveyItems, FilterCriteria, FilterOptions } from '@core/hooks/useFilterSurveyItems';
import { SurveyDetailsItem } from '@core/api/useSurveys';
import cn from 'classnames';
import useMappedSegments from '@hooks/useMappedSegments';
import { FCX } from '@models';

const datePickerSlotStyles = {
    textField: {
        InputProps: {
            slotProps: {
                input: {
                    sx: {
                        padding: '9px',
                        fontSize: '14px',
                        width: '80px'
                    }
                }
            }
        }
    },
};

export interface SurveyItemsFiltersProps {
    items: SurveyDetailsItem[];
    countryId: number;
    enableDateRange?: boolean;
    onDateRangeChange?: (startDate: Dayjs, endDate: Dayjs) => void;
    onFilteredItemsChange?: (filteredItems: SurveyDetailsItem[]) => void;
    onFilterOptionsChange?: (filterOptions: FilterOptions) => void;
    className?: string;
}

const SurveyItemsFilters: FCX<SurveyItemsFiltersProps> = ({
    items,
    countryId,
    enableDateRange = false,
    onDateRangeChange,
    onFilteredItemsChange,
    onFilterOptionsChange,
    className = '',
}) => {
    const [startDate, setStartDate] = useState<Dayjs>(dayjs().subtract(1, 'year').startOf('day'));
    const [endDate, setEndDate] = useState<Dayjs>(dayjs().endOf('day'));
    const [conceptTypes, setConceptTypes] = useState<string[]>([]);
    const [chainOrSegments, setChainOrSegments] = useState<string[]>([]);
    const [itemTypes, setItemTypes] = useState<string[]>([]);
    const [segments, setSegments] = useState<string[]>([]);
    const [searchValue, setSearchValue] = useState<string>('');

    const mappedSegments = useMappedSegments(countryId)

    // Create filter criteria object
    const filterCriteria: FilterCriteria = useMemo(() => ({
        startDate,
        endDate,
        conceptTypes,
        chainOrSegments,
        itemTypes,
        segments,
        searchValue,
        enableDateRange,
    }), [startDate, endDate, conceptTypes, chainOrSegments, itemTypes, segments, searchValue, enableDateRange]);

    // Use the filtering hook
    const {
        filteredItems,
        filterOptions
    } = useFilterSurveyItems({
        items,
        filterCriteria,
        mappedSegments
    });

    useEffect(() => {
        return () => {
            resetFilters();
        };
    }, []);

    // Notify parent of filtered items changes
    useEffect(() => {
        onFilteredItemsChange?.(filteredItems);
    }, [filteredItems, onFilteredItemsChange]);

    // Notify parent of filter options changes
    useEffect(() => {
        onFilterOptionsChange?.(filterOptions);
    }, [filterOptions, onFilterOptionsChange]);

    // Handle date range changes
    const handleStartDateChange = (date: Dayjs | null) => {
        if (date) {
            const newStartDate = date.startOf('day');
            setStartDate(newStartDate);
            onDateRangeChange?.(newStartDate, endDate);
        }
    };

    const handleEndDateChange = (date: Dayjs | null) => {
        if (date) {
            const newEndDate = date.endOf('day');
            setEndDate(newEndDate);
            onDateRangeChange?.(startDate, newEndDate);
        }
    };

    // Reset filters
    const resetFilters = () => {
        setConceptTypes([]);
        setChainOrSegments([]);
        setItemTypes([]);
        setSegments([]);
        setSearchValue('');
    };

    return (
        <div className={
            cn('SurveyItemsFilters', className)
        }>
            {enableDateRange && (
                <div className="SurveyItemsFilters__date-container">
                    Filter By:
                    <DatePicker
                        label="Start Date"
                        value={startDate}
                        onChange={handleStartDateChange}
                        slotProps={datePickerSlotStyles}
                    />
                    <DatePicker
                        label="End Date"
                        value={endDate}
                        disableFuture
                        onChange={handleEndDateChange}
                        minDate={startDate || undefined}
                        slotProps={datePickerSlotStyles}
                    />
                </div>
            )}
            
            <div className="SurveyItemsFilters__filters-container">
                <Select
                    options={filterOptions.conceptTypeOptions}
                    value={conceptTypes}
                    setValue={setConceptTypes}
                    label="Concept Type"
                    multiple
                />
                <Select
                    options={filterOptions.chainOrSegmentsOptions}
                    value={chainOrSegments}
                    setValue={setChainOrSegments}
                    label="Chain (Tested For)"
                    multiple
                />
                <Select
                    options={filterOptions.itemTypesOptions}
                    value={itemTypes}
                    setValue={setItemTypes}
                    label="Item Type"
                    multiple
                />
                <Select
                    options={filterOptions.segmentOptions}
                    value={segments}
                    setValue={setSegments}
                    label="Segment"
                    multiple
                />
            </div>
            
            <div className="SurveyItemsFilters__search-container">
                <TextInput
                    value={searchValue}
                    setValue={setSearchValue}
                    placeholder="Search items by name"
                />
            </div>
        </div>
    );
};

export default SurveyItemsFilters;
